import { useState, useEffect, useCallback } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card"
import { Button } from "../components/ui/button"
import { Badge } from "../components/ui/badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select"
import { 
  TrendingUp, 
  TrendingDown, 
  AlertTriangle, 
  Lightbulb,
  Target,
  BarChart3,
  PieChart,
  LineChart,
  DollarSign,
  Users,
  Calendar,
  Zap,
  CheckCircle2,
  XCircle,
  Wallet,
  Activity,
  Clock,
  Filter,
  RefreshCw,
  Loader2,
  ArrowRight
} from "lucide-react"
import { bankingApiV2 as bankingApi } from '@/lib/api/banking-v2'
import { bankingApi as bankingApiMain } from '@/lib/api/banking'
import { getCompanyId } from '@/lib/config'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'

interface FinancialInsight {
  id: string
  type: 'trend' | 'anomaly' | 'recommendation' | 'forecast'
  category: 'revenue' | 'expenses' | 'cash_flow' | 'profitability' | 'efficiency'
  title: string
  description: string
  impact: 'high' | 'medium' | 'low'
  confidence: number
  data: any
  actionable: boolean
  createdAt: string
}

interface IndustryBenchmark {
  industry: string
  metric: string
  value: number
  percentile: number
  comparison: 'above' | 'below' | 'average'
}

interface CashFlowForecast {
  period: string
  projectedInflow: number
  projectedOutflow: number
  netCashFlow: number
  confidence: number
  factors: string[]
}

interface AdvancedAnalyticsProps {
  companyId?: string
}

export function AdvancedAnalytics({ companyId }: AdvancedAnalyticsProps) {
  const [insights, setInsights] = useState<FinancialInsight[]>([])
  const [benchmarks, setBenchmarks] = useState<IndustryBenchmark[]>([])
  const [forecast, setForecast] = useState<CashFlowForecast[]>([])
  const [transactionAnalytics, setTransactionAnalytics] = useState<any>(null)
  const [reconciliationAnalytics, setReconciliationAnalytics] = useState<any>(null)
  const [accountAnalytics, setAccountAnalytics] = useState<any>(null)
  const [unreconciledTransactions, setUnreconciledTransactions] = useState<any[]>([])
  const [bankAccounts, setBankAccounts] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [reconciling, setReconciling] = useState(false)
  const [selectedIndustry, setSelectedIndustry] = useState('retail')
  const [activeTab, setActiveTab] = useState('transactions')
  const [period, setPeriod] = useState('30')
  
  // Filters for reconciliation tab
  const [reconciliationFilters, setReconciliationFilters] = useState({
    accountId: 'all',
    status: 'unreconciled',
    dateFrom: '',
    dateTo: ''
  })

  const currentCompanyId = companyId || getCompanyId()

  const industries = [
    { value: 'retail', label: 'Retail' },
    { value: 'saas', label: 'SaaS/Software' },
    { value: 'consulting', label: 'Consulting' },
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'general', label: 'General Business' }
  ]

  useEffect(() => {
    if (currentCompanyId) {
      loadAnalyticsData()
      loadBankAccounts()
    }
  }, [selectedIndustry, currentCompanyId, period])

  useEffect(() => {
    if (currentCompanyId && activeTab === 'reconciliation') {
      loadUnreconciledTransactions()
    }
  }, [currentCompanyId, activeTab, reconciliationFilters])

  const loadBankAccounts = useCallback(async () => {
    if (!currentCompanyId) return
    try {
      const accounts = await bankingApiMain.getBankAccounts(currentCompanyId)
      setBankAccounts(accounts || [])
    } catch (error) {
      console.error('Error loading bank accounts:', error)
    }
  }, [currentCompanyId])

  const loadUnreconciledTransactions = useCallback(async () => {
    if (!currentCompanyId) return
    try {
      const params = new URLSearchParams()
      params.append('companyId', currentCompanyId)
      params.append('status', reconciliationFilters.status)
      params.append('page', '1')
      params.append('pageSize', '50')
      
      if (reconciliationFilters.accountId !== 'all') {
        params.append('bankAccountId', reconciliationFilters.accountId)
      }
      
      const response = await bankingApiMain.getBankTransactions(
        reconciliationFilters.accountId !== 'all' ? reconciliationFilters.accountId : undefined,
        currentCompanyId,
        reconciliationFilters.status,
        1,
        50
      )
      
      let transactions = response?.items || []
      
      // Apply date filters client-side
      if (reconciliationFilters.dateFrom) {
        const fromDate = new Date(reconciliationFilters.dateFrom)
        transactions = transactions.filter((t: any) => new Date(t.transactionDate) >= fromDate)
      }
      if (reconciliationFilters.dateTo) {
        const toDate = new Date(reconciliationFilters.dateTo)
        toDate.setHours(23, 59, 59, 999)
        transactions = transactions.filter((t: any) => new Date(t.transactionDate) <= toDate)
      }
      
      setUnreconciledTransactions(transactions)
    } catch (error) {
      console.error('Error loading unreconciled transactions:', error)
    }
  }, [currentCompanyId, reconciliationFilters])

  const loadAnalyticsData = useCallback(async () => {
    if (!currentCompanyId) return
    
    setLoading(true)
    try {
      // Load banking-specific analytics
      const [transactionsRes, reconciliationRes, accountsRes, insightsRes, benchmarksRes, forecastRes] = await Promise.all([
        bankingApi.get(`/api/analytics/transactions?companyId=${currentCompanyId}&period=${period}`).catch(() => ({ success: false, analytics: null })),
        bankingApi.get(`/api/analytics/reconciliation?companyId=${currentCompanyId}`).catch(() => ({ success: false, analytics: null })),
        bankingApi.get(`/api/analytics/accounts?companyId=${currentCompanyId}`).catch(() => ({ success: false, analytics: null })),
        bankingApi.get(`/api/analytics/insights?companyId=${currentCompanyId}&industry=${selectedIndustry}`).catch(() => ({ success: false, insights: [] })),
        bankingApi.get(`/api/analytics/benchmarks/${selectedIndustry}`).catch(() => ({ success: false, benchmarks: [] })),
        bankingApi.get(`/api/analytics/cash-flow-forecast?companyId=${currentCompanyId}&months=6`).catch(() => ({ success: false, forecast: [] }))
      ])

      if (transactionsRes?.success && transactionsRes.analytics) {
        setTransactionAnalytics(transactionsRes.analytics)
      }
      if (reconciliationRes?.success && reconciliationRes.analytics) {
        setReconciliationAnalytics(reconciliationRes.analytics)
      }
      if (accountsRes?.success && accountsRes.analytics) {
        setAccountAnalytics(accountsRes.analytics)
      }
      if (insightsRes?.success) {
        setInsights(insightsRes.insights || [])
      }
      if (benchmarksRes?.success) {
        setBenchmarks(benchmarksRes.benchmarks || [])
      }
      if (forecastRes?.success) {
        setForecast(forecastRes.forecast || [])
      }
    } catch (error) {
      console.error('Error loading analytics data:', error)
    } finally {
      setLoading(false)
    }
  }, [selectedIndustry, currentCompanyId, period])

  const handleAutoReconcileAll = async () => {
    if (!currentCompanyId) return
    setReconciling(true)
    try {
      const result = await bankingApiMain.autoReconcileAll(currentCompanyId)
      console.log('Auto-reconcile result:', result)
      // Reload data
      await loadAnalyticsData()
      await loadUnreconciledTransactions()
    } catch (error) {
      console.error('Error auto-reconciling:', error)
    } finally {
      setReconciling(false)
    }
  }

  const handleReconcileTransaction = async (transactionId: string) => {
    if (!currentCompanyId) return
    try {
      await bankingApiMain.reconcileTransaction(transactionId, {})
      // Reload data
      await loadAnalyticsData()
      await loadUnreconciledTransactions()
    } catch (error) {
      console.error('Error reconciling transaction:', error)
    }
  }

  const formatCurrency = (amount: number, currency: string = 'RWF') => {
    return new Intl.NumberFormat('en-RW', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  const getInsightIcon = (type: string) => {
    switch (type) {
      case 'trend': return <TrendingUp className="w-5 h-5 text-blue-600" />
      case 'anomaly': return <AlertTriangle className="w-5 h-5 text-orange-600" />
      case 'recommendation': return <Lightbulb className="w-5 h-5 text-green-600" />
      case 'forecast': return <Target className="w-5 h-5 text-purple-600" />
      default: return <BarChart3 className="w-5 h-5 text-gray-600" />
    }
  }

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'high': return 'text-red-600 bg-red-100'
      case 'medium': return 'text-yellow-600 bg-yellow-100'
      case 'low': return 'text-green-600 bg-green-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'revenue': return 'text-green-600'
      case 'expenses': return 'text-red-600'
      case 'cash_flow': return 'text-blue-600'
      case 'profitability': return 'text-purple-600'
      case 'efficiency': return 'text-orange-600'
      default: return 'text-gray-600'
    }
  }

  const getComparisonIcon = (comparison: string) => {
    switch (comparison) {
      case 'above': return <TrendingUp className="w-4 h-4 text-green-600" />
      case 'below': return <TrendingDown className="w-4 h-4 text-red-600" />
      case 'average': return <BarChart3 className="w-4 h-4 text-blue-600" />
      default: return <BarChart3 className="w-4 h-4 text-gray-600" />
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded w-1/2"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Advanced Analytics</h2>
        <div className="flex items-center gap-4">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="180">Last 6 months</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={loadAnalyticsData} variant="outline">
            Refresh Data
          </Button>
        </div>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="transactions">Transactions</TabsTrigger>
          <TabsTrigger value="reconciliation">Reconciliation</TabsTrigger>
          <TabsTrigger value="accounts">Bank Accounts</TabsTrigger>
          <TabsTrigger value="insights">Financial Insights</TabsTrigger>
          <TabsTrigger value="forecast">Forecast</TabsTrigger>
        </TabsList>

        {/* Transactions Analytics */}
        <TabsContent value="transactions" className="space-y-4">
          {transactionAnalytics ? (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-2">
                      <Activity className="w-5 h-5 text-blue-600" />
                      <span className="text-sm font-medium">Total Transactions</span>
                    </div>
                    <div className="text-2xl font-bold">
                      {transactionAnalytics.totalTransactions || 0}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="w-5 h-5 text-green-600" />
                      <span className="text-sm font-medium">Total Inflow</span>
                    </div>
                    <div className="text-2xl font-bold text-green-600">
                      {formatCurrency(transactionAnalytics.totalInflow || 0)}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingDown className="w-5 h-5 text-red-600" />
                      <span className="text-sm font-medium">Total Outflow</span>
                    </div>
                    <div className="text-2xl font-bold text-red-600">
                      {formatCurrency(transactionAnalytics.totalOutflow || 0)}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-2">
                      <DollarSign className="w-5 h-5 text-purple-600" />
                      <span className="text-sm font-medium">Net Cash Flow</span>
                    </div>
                    <div className={`text-2xl font-bold ${
                      (transactionAnalytics.netCashFlow || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {formatCurrency(transactionAnalytics.netCashFlow || 0)}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Top Merchants */}
              {transactionAnalytics.topMerchants && transactionAnalytics.topMerchants.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Top Merchants</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {transactionAnalytics.topMerchants.slice(0, 10).map((merchant: any, index: number) => (
                        <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                              <span className="text-sm font-bold text-blue-600">{index + 1}</span>
                            </div>
                            <div>
                              <div className="font-medium">{merchant.name}</div>
                              <div className="text-sm text-muted-foreground">{merchant.count} transactions</div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-bold">{formatCurrency(merchant.total)}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Transactions by Category */}
              {transactionAnalytics.transactionsByCategory && Object.keys(transactionAnalytics.transactionsByCategory).length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Transactions by Category</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {Object.entries(transactionAnalytics.transactionsByCategory)
                        .sort(([, a]: any, [, b]: any) => b - a)
                        .slice(0, 10)
                        .map(([category, amount]: [string, any]) => (
                          <div key={category} className="flex items-center justify-between p-3 border rounded-lg">
                            <div className="font-medium capitalize">{category}</div>
                            <div className="font-bold">{formatCurrency(amount)}</div>
                          </div>
                        ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card>
              <CardContent className="p-6 text-center text-muted-foreground">
                No transaction data available
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Reconciliation Analytics */}
        <TabsContent value="reconciliation" className="space-y-4">
          {reconciliationAnalytics ? (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-2">
                      <Activity className="w-5 h-5 text-blue-600" />
                      <span className="text-sm font-medium">Total Transactions</span>
                    </div>
                    <div className="text-2xl font-bold">
                      {reconciliationAnalytics.totalTransactions || 0}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                      <span className="text-sm font-medium">Reconciled</span>
                    </div>
                    <div className="text-2xl font-bold text-green-600">
                      {reconciliationAnalytics.reconciledTransactions || 0}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {reconciliationAnalytics.reconciliationRate?.toFixed(1) || 0}% rate
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-2">
                      <XCircle className="w-5 h-5 text-red-600" />
                      <span className="text-sm font-medium">Unreconciled</span>
                    </div>
                    <div className="text-2xl font-bold text-red-600">
                      {reconciliationAnalytics.unreconciledTransactions || 0}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-2">
                      <Zap className="w-5 h-5 text-purple-600" />
                      <span className="text-sm font-medium">Auto-Reconciled</span>
                    </div>
                    <div className="text-2xl font-bold text-purple-600">
                      {reconciliationAnalytics.autoReconciledCount || 0}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-5 h-5 text-orange-600" />
                      <span className="text-sm font-medium">Avg. Time</span>
                    </div>
                    <div className="text-2xl font-bold text-orange-600">
                      {reconciliationAnalytics.averageReconciliationTime || 0}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      days
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Quick Actions */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Quick Actions</CardTitle>
                    <Button 
                      onClick={handleAutoReconcileAll} 
                      disabled={reconciling || (reconciliationAnalytics.unreconciledTransactions || 0) === 0}
                      variant="default"
                    >
                      {reconciling ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Reconciling...
                        </>
                      ) : (
                        <>
                          <Zap className="w-4 h-4 mr-2" />
                          Auto-Reconcile All
                        </>
                      )}
                    </Button>
                  </div>
                </CardHeader>
              </Card>

              {/* Filters */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Filter className="w-5 h-5" />
                    Filters
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <Label htmlFor="account-filter">Bank Account</Label>
                      <Select 
                        value={reconciliationFilters.accountId} 
                        onValueChange={(value) => setReconciliationFilters({ ...reconciliationFilters, accountId: value })}
                      >
                        <SelectTrigger id="account-filter">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Accounts</SelectItem>
                          {bankAccounts.map((account) => (
                            <SelectItem key={account.id} value={account.id}>
                              {account.bankName} - {account.accountNumber}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="status-filter">Status</Label>
                      <Select 
                        value={reconciliationFilters.status} 
                        onValueChange={(value) => setReconciliationFilters({ ...reconciliationFilters, status: value })}
                      >
                        <SelectTrigger id="status-filter">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unreconciled">Unreconciled</SelectItem>
                          <SelectItem value="reconciled">Reconciled</SelectItem>
                          <SelectItem value="all">All</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="date-from">Date From</Label>
                      <Input
                        id="date-from"
                        type="date"
                        value={reconciliationFilters.dateFrom}
                        onChange={(e) => setReconciliationFilters({ ...reconciliationFilters, dateFrom: e.target.value })}
                      />
                    </div>

                    <div>
                      <Label htmlFor="date-to">Date To</Label>
                      <Input
                        id="date-to"
                        type="date"
                        value={reconciliationFilters.dateTo}
                        onChange={(e) => setReconciliationFilters({ ...reconciliationFilters, dateTo: e.target.value })}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Reconciliation Timeline */}
              {reconciliationAnalytics.reconciliationByMonth && reconciliationAnalytics.reconciliationByMonth.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Reconciliation Timeline</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {reconciliationAnalytics.reconciliationByMonth.map((month: any, index: number) => (
                        <div key={index} className="space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="font-medium">{new Date(month.month + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</div>
                            <div className="text-sm text-muted-foreground">
                              {month.reconciled} / {month.reconciled + month.unreconciled} ({month.rate.toFixed(1)}%)
                            </div>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-green-600 h-2 rounded-full transition-all"
                              style={{ width: `${month.rate}%` }}
                            ></div>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3 text-green-600" />
                              {month.reconciled} reconciled
                            </span>
                            <span className="flex items-center gap-1">
                              <XCircle className="w-3 h-3 text-red-600" />
                              {month.unreconciled} unreconciled
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Reconciliation by Account */}
              {reconciliationAnalytics.reconciliationByAccount && reconciliationAnalytics.reconciliationByAccount.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Reconciliation by Account</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {reconciliationAnalytics.reconciliationByAccount.map((account: any, index: number) => (
                        <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                          <div>
                            <div className="font-medium">{account.accountName}</div>
                            <div className="text-sm text-muted-foreground">
                              {account.reconciled} reconciled, {account.unreconciled} unreconciled
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-bold">{account.rate.toFixed(1)}%</div>
                            <Badge variant={account.rate >= 80 ? 'default' : account.rate >= 50 ? 'secondary' : 'destructive'}>
                              {account.rate >= 80 ? 'Good' : account.rate >= 50 ? 'Fair' : 'Needs Attention'}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Unreconciled Transactions */}
              {unreconciledTransactions.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Unreconciled Transactions ({unreconciledTransactions.length})</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {unreconciledTransactions.map((transaction: any) => (
                        <div key={transaction.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                          <div className="flex-1">
                            <div className="flex items-center gap-3">
                              <div className={`w-2 h-2 rounded-full ${Number(transaction.amount) > 0 ? 'bg-green-500' : 'bg-red-500'}`}></div>
                              <div>
                                <div className="font-medium">{transaction.description || transaction.merchantName || 'No description'}</div>
                                <div className="text-sm text-muted-foreground">
                                  {new Date(transaction.transactionDate).toLocaleDateString()} • {transaction.bankAccount?.bankName || 'Unknown Account'}
                                  {transaction.reference && ` • Ref: ${transaction.reference}`}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <div className={`font-bold ${Number(transaction.amount) > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {formatCurrency(Math.abs(Number(transaction.amount)), transaction.currency || 'RWF')}
                              </div>
                              <Badge variant="destructive" className="mt-1">Unreconciled</Badge>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleReconcileTransaction(transaction.id)}
                              className="flex items-center gap-2"
                            >
                              <CheckCircle2 className="w-4 h-4" />
                              Reconcile
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card>
              <CardContent className="p-6 text-center text-muted-foreground">
                No reconciliation data available
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Bank Accounts Analytics */}
        <TabsContent value="accounts" className="space-y-4">
          {accountAnalytics ? (
            <>
              {/* Summary Cards */}
              {accountAnalytics.summary && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-2 mb-2">
                        <Wallet className="w-5 h-5 text-blue-600" />
                        <span className="text-sm font-medium">Total Accounts</span>
                      </div>
                      <div className="text-2xl font-bold">
                        {accountAnalytics.summary.totalAccounts || 0}
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-2 mb-2">
                        <DollarSign className="w-5 h-5 text-green-600" />
                        <span className="text-sm font-medium">Total Balance</span>
                      </div>
                      <div className="text-2xl font-bold text-green-600">
                        {formatCurrency(accountAnalytics.summary.totalBalance || 0)}
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="w-5 h-5 text-blue-600" />
                        <span className="text-sm font-medium">Total Inflow</span>
                      </div>
                      <div className="text-2xl font-bold text-blue-600">
                        {formatCurrency(accountAnalytics.summary.totalInflow || 0)}
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-2 mb-2">
                        <Activity className="w-5 h-5 text-purple-600" />
                        <span className="text-sm font-medium">Total Transactions</span>
                      </div>
                      <div className="text-2xl font-bold text-purple-600">
                        {accountAnalytics.summary.totalTransactions || 0}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Account Details */}
              {accountAnalytics.accounts && accountAnalytics.accounts.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Account Details</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {accountAnalytics.accounts.map((account: any, index: number) => (
                        <div key={index} className="p-4 border rounded-lg">
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <div className="font-bold text-lg">{account.accountName}</div>
                              <div className="text-sm text-muted-foreground">{account.accountNumber}</div>
                            </div>
                            <div className="text-right">
                              <div className="text-2xl font-bold">{formatCurrency(account.currentBalance, account.currency)}</div>
                              <Badge variant={account.balanceTrend === 'increasing' ? 'default' : account.balanceTrend === 'decreasing' ? 'destructive' : 'secondary'}>
                                {account.balanceTrend}
                              </Badge>
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-4 mt-3 pt-3 border-t">
                            <div>
                              <div className="text-sm text-muted-foreground">Inflow</div>
                              <div className="font-medium text-green-600">{formatCurrency(account.totalInflow, account.currency)}</div>
                            </div>
                            <div>
                              <div className="text-sm text-muted-foreground">Outflow</div>
                              <div className="font-medium text-red-600">{formatCurrency(account.totalOutflow, account.currency)}</div>
                            </div>
                            <div>
                              <div className="text-sm text-muted-foreground">Transactions</div>
                              <div className="font-medium">{account.transactionCount}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card>
              <CardContent className="p-6 text-center text-muted-foreground">
                No account data available
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Financial Insights */}
        <TabsContent value="insights" className="space-y-4">
          <div className="space-y-4">
            {insights.length > 0 ? insights.map((insight) => (
              <Card key={insight.id} className="border-l-4 border-l-blue-500">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      {getInsightIcon(insight.type)}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold">{insight.title}</h3>
                          <Badge className={getImpactColor(insight.impact)}>
                            {insight.impact} impact
                          </Badge>
                          <Badge variant="outline" className={getCategoryColor(insight.category)}>
                            {insight.category.replace('_', ' ')}
                          </Badge>
                        </div>
                        <p className="text-muted-foreground mb-3">{insight.description}</p>
                        {insight.actionable && (
                          <div className="flex items-center gap-2 text-sm text-green-600">
                            <Zap className="w-4 h-4" />
                            Actionable insight
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium">
                        {Math.round((insight.confidence || 0) * 100)}% confidence
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {insight.createdAt ? new Date(insight.createdAt).toLocaleDateString() : 'N/A'}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )) : (
              <Card>
                <CardContent className="p-6 text-center text-muted-foreground">
                  No insights available
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Cash Flow Forecast */}
        <TabsContent value="forecast" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <LineChart className="w-5 h-5 text-orange-600" />
                6-Month Cash Flow Forecast
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {forecast && forecast.length > 0 ? forecast.map((month, index) => {
                  if (!month) return null;
                  
                  return (
                    <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <Calendar className="w-5 h-5 text-blue-600" />
                        <div>
                          <div className="font-medium">{month.period || 'N/A'}</div>
                          <div className="text-sm text-muted-foreground">
                            Confidence: {Math.round((month.confidence || 0) * 100)}%
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-6">
                        <div className="text-center">
                          <div className="text-sm text-muted-foreground">Inflow</div>
                          <div className="font-medium text-green-600">
                            {formatCurrency(month.projectedInflow || 0)}
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-sm text-muted-foreground">Outflow</div>
                          <div className="font-medium text-red-600">
                            {formatCurrency(month.projectedOutflow || 0)}
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-sm text-muted-foreground">Net</div>
                          <div className={`font-medium ${
                            (month.netCashFlow || 0) > 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {formatCurrency(month.netCashFlow || 0)}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }) : (
                  <div className="text-center py-8 text-muted-foreground">
                    No forecast data available
                  </div>
                )}
              </div>
              
              {forecast && forecast[0]?.factors && (
                <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-medium mb-2">Forecast Factors</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    {forecast[0].factors.map((factor, index) => (
                      <li key={index} className="flex items-center gap-2">
                        <div className="w-1 h-1 bg-blue-600 rounded-full"></div>
                        {factor}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
